import { useState, useEffect, useRef } from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import {
    FaXmark,
    FaCalendarDays,
    FaArrowDown,
    FaArrowUp,
    FaMoneyBillWave,
    FaCircleInfo,
    FaSpinner,
    FaCheck,
    FaArrowRightArrowLeft,
    FaUserTie,
} from "react-icons/fa6";
import { SelectTransactionCategory } from "../../../../../components/forms/selects/SelectTransactionCategory";
import { SelectPaymentMethod } from "../../../../../components/forms/selects/SelectPaymentMethod";
import { TransactionService } from "../../../../../services/TransactionService";
import { PiutangService } from "../../../../../services/PiutangService";
import { SelectUser } from "../../../../../components/forms/selects/SelectUser";

/* ============================================
   OPTIONS
   ============================================ */
const TIPE_OPTIONS = [
    { value: "masuk", label: "Bayar Piutang", icon: FaArrowDown, color: "emerald" },
    { value: "keluar", label: "Tambah Piutang", icon: FaArrowUp, color: "red" },
];

/* ============================================
   UTILITIES
   ============================================ */
const formatRupiahInput = (value) => {
    const num = value.replace(/\D/g, "");
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const getTodayDate = () => {
    const now = new Date();
    return now.toISOString().split("T")[0];
};

const initialFormData = {
    user: null,
    type: "masuk",
    category_id: null,
    date_time: getTodayDate(),
    payment_method_id: null,
    bank_account_id: null,
    description: "",
};

/* ============================================
   KOMPONEN: Input Field
   ============================================ */
const InputField = ({ label, icon, error, children, required }) => (
    <div className="space-y-1.5">
        <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
            {icon && <span className="text-slate-500">{icon}</span>}
            {label}
            {required && <span className="text-red-400">*</span>}
        </label>
        {children}
        {error && (
            <p className="flex items-center gap-1.5 text-xs text-red-400 mt-1">
                <FaExclamationTriangle className="text-[10px] flex-shrink-0" />
                {error}
            </p>
        )}
    </div>
);

/* ============================================
   KOMPONEN: Toast
   ============================================ */
const Toast = ({ type, message, onClose }) => {
    const isSuccess = type === "success";

    useEffect(() => {
        const timer = setTimeout(onClose, 4000);
        return () => clearTimeout(timer);
    }, [onClose]);

    return (
        <div
            className={`fixed top-6 right-6 z-[100] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl border animate-[slideIn_0.3s_ease-out] ${isSuccess
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                : "bg-red-500/10 border-red-500/30 text-red-300"
                }`}
            style={{ backdropFilter: "blur(16px)" }}
        >
            <div
                className={`p-1.5 rounded-xl ${isSuccess ? "bg-emerald-500/20" : "bg-red-500/20"
                    }`}
            >
                {isSuccess ? (
                    <FaCheck className="text-emerald-400 text-sm" />
                ) : (
                    <FaExclamationTriangle className="text-red-400 text-sm" />
                )}
            </div>
            <span className="text-sm font-medium">{message}</span>
            <button
                onClick={onClose}
                className="ml-2 opacity-60 hover:opacity-100 transition-opacity"
            >
                <FaXmark className="text-sm" />
            </button>
        </div>
    );
};

/* ============================================
   MAIN: TransactionCreate Modal
   ============================================ */
export const PiutangCreate = ({ open, onClose, onSuccess }) => {
    const overlayRef = useRef(null);
    const contentRef = useRef(null);

    /* ---------- State ---------- */
    const [formData, setFormData] = useState(initialFormData);
    const [nominalRaw, setNominalRaw] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState(null);
    const [errors, setErrors] = useState({});

    /* ---------- Reset form saat modal buka ---------- */
    useEffect(() => {
        if (open) {
            setFormData(initialFormData);
            setNominalRaw("");
            setErrors({});
        }
    }, [open]);

    /* ---------- Lock body scroll ---------- */
    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    /* ---------- Close on Escape ---------- */
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape" && open && !submitting) {
                handleClose();
            }
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [open, submitting]);

    /* ---------- Helper: update field + hapus error ---------- */
    const updateField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    /* ---------- Close modal ---------- */
    const handleClose = () => {
        if (submitting) return;
        setFormData(initialFormData);
        setNominalRaw("");
        setErrors({});
        onClose();
    };

    /* ---------- Close on overlay click ---------- */
    const handleOverlayClick = (e) => {
        if (e.target === overlayRef.current) {
            handleClose();
        }
    };

    /* ---------- Validation ---------- */
    const validate = () => {
        const newErrors = {};

        if (!formData.user) {
            newErrors.user = "Pilih kreditur";
        }

        if (!formData.type) {
            newErrors.type = "Pilih tipe transaksi";
        }

        if (!formData.date_time) {
            newErrors.date_time = "Tanggal wajib diisi";
        }

        const nominalNum = parseInt(nominalRaw.replace(/\D/g, ""), 10);
        if (!nominalRaw || isNaN(nominalNum) || nominalNum <= 0) {
            newErrors.nominal = "Nominal harus lebih dari 0";
        } else if (nominalNum > 999999999) {
            newErrors.nominal = "Nominal maksimal 999.999.999";
        }

        if (!formData.payment_method_id) {
            newErrors.payment_method_id = "Pilih metode pembayaran";
        }

        if (!formData.description.trim()) {
            newErrors.description = "Keterangan wajib diisi";
        } else if (formData.description.trim().length < 3) {
            newErrors.description = "Keterangan minimal 3 karakter";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /* ---------- Submit ---------- */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        setErrors({});
        const payload = {
            user_id: formData?.user?.id,
            type: formData.type,
            category_id: formData.category_id,
            date_time: formData.date_time,
            nominal: parseInt(nominalRaw.replace(/\D/g, ""), 10),
            payment_method_id: formData.payment_method_id,
            bank_account_id: formData.bank_account_id,
            description: formData.description.trim(),
        };
        try {


            const res = await PiutangService.create({ data: payload });

            setToast({
                type: "success",
                message: res.data?.message_v2 || "Transaksi berhasil ditambahkan!",
            });

            if (onSuccess) onSuccess(res.data?.data);

            setTimeout(() => {
                handleClose();
            }, 1200);
        } catch (error) {
            // const errData = error.response?.data;
            // if (errData?.errors && typeof errData.errors === "object") {
            //     // Laravel validation errors → map ke field
            //     const serverErrors = {};
            //     for (const [key, messages] of Object.entries(errData.errors)) {
            //         serverErrors[key] = Array.isArray(messages) ? messages[0] : messages;
            //     }
            //     setErrors(serverErrors);
            //     setToast({
            //         type: "error",
            //         message: errData.message_v2 || "Periksa kembali data yang diisi",
            //     });
            // } else {
            //     setToast({
            //         type: "error",
            //         message: errData?.message_v2 || "Terjadi kesalahan pada server",
            //     });
            // }
        } finally {
            setSubmitting(false);
        }
    };

    /* ---------- Don't render if not open ---------- */
    if (!open) return null;

    return (
        <>
            {/* ===== Overlay ===== */}
            <div
                ref={overlayRef}
                onClick={handleOverlayClick}
                className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto px-4 py-8 sm:py-12"
                style={{
                    background: "rgba(2, 6, 23, 0.75)",
                    backdropFilter: "blur(6px)",
                }}
            >
                {/* ===== Modal Content ===== */}
                <div
                    ref={contentRef}
                    className="w-full max-w-lg rounded-2xl overflow-hidden animate-[scaleIn_0.25s_ease-out]"
                    style={{
                        background: "rgba(15, 23, 42, 0.95)",
                        border: "1px solid rgba(51, 65, 85, 0.4)",
                        boxShadow:
                            "0 25px 60px -12px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(51, 65, 85, 0.1)",
                    }}
                >
                    {/* ---- Header ---- */}
                    <div
                        className="flex items-center justify-between px-5 sm:px-6 py-4"
                        style={{
                            borderBottom: "1px solid rgba(51, 65, 85, 0.3)",
                            background: "rgba(15, 23, 42, 0.5)",
                        }}
                    >
                        <div className="flex items-center gap-3">
                            <div
                                className="p-2 rounded-xl"
                                style={{
                                    background: "rgba(0, 180, 216, 0.1)",
                                    border: "1px solid rgba(0, 180, 216, 0.15)",
                                }}
                            >
                                <FaMoneyBillWave className="text-cyan-400 text-sm" />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-gray-100">
                                    Tambah Transaksi
                                </h2>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    Isi data transaksi keuangan baru
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            disabled={submitting}
                            className="p-2 rounded-xl text-slate-400 hover:text-gray-100 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                        >
                            <FaXmark className="text-lg" />
                        </button>
                    </div>

                    {/* ---- Form ---- */}
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="px-5 sm:px-6 py-5 space-y-5">

                            {/* === User === */}
                            <div className="space-y-1.5">
                                <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                                    <span className="text-slate-500">
                                        <FaUserTie className="text-xs" />
                                    </span>
                                    Kreditur
                                    <span className="text-red-400">*</span>
                                </label>
                                <SelectUser
                                    value={formData.user}
                                    onChange={(value) => {
                                        setFormData((prev) => ({
                                            ...prev,
                                            user: value,
                                        }));
                                    }}
                                    placeholder="Pilih kreditur"
                                />
                                {errors.user_id && (
                                    <p className="flex items-center gap-1.5 text-xs text-red-400 mt-1">
                                        <FaExclamationTriangle className="text-[10px] flex-shrink-0" />
                                        {errors.user_id}
                                    </p>
                                )}
                            </div>

                            {/* === Tipe Transaksi === */}
                            <InputField
                                label="Tipe Transaksi"
                                icon={<FaArrowRightArrowLeft className="text-xs" />}
                                error={errors.type}
                                required
                            >
                                <div className="grid grid-cols-2 gap-3">
                                    {TIPE_OPTIONS.map((opt) => {
                                        const Icon = opt.icon;
                                        const isActive = formData.type === opt.value;
                                        return (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => updateField("type", opt.value)}
                                                className={`relative flex items-center justify-center gap-2.5 py-3.5 rounded-xl text-sm font-semibold border transition-all duration-200 ${isActive
                                                    ? opt.color === "emerald"
                                                        ? "bg-emerald-500/12 border-emerald-500/40 text-emerald-400 shadow-lg shadow-emerald-500/10"
                                                        : "bg-red-500/12 border-red-500/40 text-red-400 shadow-lg shadow-red-500/10"
                                                    : "bg-slate-800/40 border-slate-600/30 text-slate-400 hover:border-slate-500/40 hover:text-slate-300"
                                                    }`}
                                            >
                                                <Icon className="text-sm" />
                                                {opt.label}
                                                {isActive && (
                                                    <div
                                                        className={`absolute top-2 right-2 w-2 h-2 rounded-full ${opt.color === "emerald"
                                                            ? "bg-emerald-400"
                                                            : "bg-red-400"
                                                            }`}
                                                    />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </InputField>



                            {/* === Kategori & Metode Pembayaran === */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {/* === Tanggal === */}
                                <InputField
                                    label="Tanggal"
                                    icon={<FaCalendarDays className="text-xs" />}
                                    error={errors.date_time}
                                    required
                                >
                                    <input
                                        type="date"
                                        value={formData.date_time}
                                        onChange={(e) => updateField("date_time", e.target.value)}
                                        className="w-full px-3.5 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all [color-scheme:dark]"
                                    />
                                </InputField>

                                {/* Payment Method */}
                                <div className="space-y-1.5">
                                    <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                                        <span className="text-slate-500">
                                            <FaMoneyBillWave className="text-xs" />
                                        </span>
                                        Metode Pembayaran
                                        <span className="text-red-400">*</span>
                                    </label>
                                    <SelectPaymentMethod
                                        value={formData.payment_method_id}
                                        searchable={false}
                                        setValue={(value) => updateField("payment_method_id", value)}
                                    />
                                    {errors.payment_method_id && (
                                        <p className="flex items-center gap-1.5 text-xs text-red-400 mt-1">
                                            <FaExclamationTriangle className="text-[10px] flex-shrink-0" />
                                            {errors.payment_method_id}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* === Nominal === */}
                            <InputField
                                label="Nominal"
                                icon={<FaMoneyBillWave className="text-xs" />}
                                error={errors.nominal}
                                required
                            >
                                <div className="relative">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-500">
                                        Rp.
                                    </span>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={nominalRaw ? formatRupiahInput(nominalRaw) : ""}
                                        onChange={(e) => {
                                            const raw = e.target.value.replace(/\D/g, "");
                                            setNominalRaw(raw);
                                            if (errors.nominal) {
                                                setErrors((prev) => {
                                                    const next = { ...prev };
                                                    delete next.nominal;
                                                    return next;
                                                });
                                            }
                                        }}
                                        placeholder="0"
                                        className="w-full pl-12 pr-20 py-2.5 text-sm font-bold rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-600 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                                    />
                                    {nominalRaw && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                            <span
                                                className={`text-[11px] font-semibold px-2 py-0.5 rounded-md ${formData.type === "masuk"
                                                    ? "text-emerald-400 bg-emerald-500/10"
                                                    : formData.type === "keluar"
                                                        ? "text-red-400 bg-red-500/10"
                                                        : "text-slate-500 bg-slate-500/10"
                                                    }`}
                                            >
                                                {formData.type === "masuk"
                                                    ? "Pemasukan"
                                                    : formData.type === "keluar"
                                                        ? "Pengeluaran"
                                                        : "-"}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </InputField>

                            {/* === Keterangan === */}
                            <InputField
                                label="Keterangan"
                                icon={<FaCircleInfo className="text-xs" />}
                                error={errors.description}
                                required
                            >
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => updateField("description", e.target.value)}
                                    rows={3}
                                    maxLength={255}
                                    placeholder="Contoh: Pembayaran tagihan Ahmad Fauzi bulan Januari"
                                    className="w-full px-3.5 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-600 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all resize-none leading-relaxed"
                                />
                                <div className="flex justify-end mt-1">
                                    <span className="text-[11px] text-slate-600">
                                        {formData.description.length}/255
                                    </span>
                                </div>
                            </InputField>
                        </div>

                        {/* ---- Footer ---- */}
                        <div
                            className="flex items-center justify-end gap-3 px-5 sm:px-6 py-4"
                            style={{
                                borderTop: "1px solid rgba(51, 65, 85, 0.3)",
                                background: "rgba(15, 23, 42, 0.5)",
                            }}
                        >
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={submitting}
                                className="px-5 py-2.5 rounded-xl text-sm font-semibold text-slate-300 bg-slate-800/60 border border-slate-600/40 hover:bg-slate-700/40 hover:text-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                            >
                                Batal
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-lg shadow-cyan-600/20"
                            >
                                {submitting ? (
                                    <>
                                        <FaSpinner className="text-xs animate-spin" />
                                        Menyimpan...
                                    </>
                                ) : (
                                    <>
                                        <FaCheck className="text-xs" />
                                        Simpan Transaksi
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* ===== Toast ===== */}
            {toast && (
                <Toast
                    type={toast.type}
                    message={toast.message}
                    onClose={() => setToast(null)}
                />
            )}

            {/* ===== Keyframes ===== */}
            <style>{`
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
        </>
    );
};